import React, { useState, useEffect } from 'react';
import { addDaysYMD, getWeekStart } from '../lib/utils';
import { ownerOperatorsApi, unitsApi, companyApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { 
  UserCheck, 
  Plus, 
  Pencil, 
  Trash2,
  Search,
  Phone,
  Mail,
  Truck,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  TrendingUp,
  Building2,
  FileText,
  Download,
  Calendar,
  Package,
  X
} from 'lucide-react';
import { toast } from 'sonner';

export default function OwnerOperators() {
  const { canEdit, isAdmin } = useAuth();
  const [ownerOperators, setOwnerOperators] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOO, setEditingOO] = useState(null);
  const [expandedOO, setExpandedOO] = useState({});
  const [summaries, setSummaries] = useState({});
  const [loadingSummary, setLoadingSummary] = useState({});
  // Whether YTD blocks should be shown (mirrors Company Settings toggle)
  const [showYtd, setShowYtd] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state: '',
    zipcode: '',
    tax_id: '',
    mc_number: '',
    dot_number: '',
    status: 'active',
    unit_ids: [],
    notes: '',
  });

  // Get current week start (Monday)
  const getCurrentWeekStart = () => {
    return getWeekStart(new Date());
  };

  const [weekStart, setWeekStart] = useState(getCurrentWeekStart());
  const [endDate, setEndDate] = useState(null); // For date range selection
  const [isDateRange, setIsDateRange] = useState(false);
  
  // PDF Export Dialog state
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfOOId, setPdfOOId] = useState(null);
  const [pdfOOName, setPdfOOName] = useState('');
  const [pdfStartDate, setPdfStartDate] = useState('');
  const [pdfEndDate, setPdfEndDate] = useState('');
  const [itemizedDeductions, setItemizedDeductions] = useState(false);

  // PDF Viewer state (preview modal shown after generation — mirrors Dashboard flow)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('settlement.pdf');
  const [pdfViewerTitle, setPdfViewerTitle] = useState('');

  useEffect(() => {
    loadData();
    // Load company YTD toggle
    companyApi.get()
      .then((r) => setShowYtd(!!r.data?.show_ytd_on_settlements))
      .catch(() => setShowYtd(false));
  }, []);

  const loadData = async () => {
    try {
      const [ooRes, unitsRes] = await Promise.all([
        ownerOperatorsApi.getAll(),
        unitsApi.getAll()
      ]);
      setOwnerOperators(ooRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async (ooId) => {
    const cacheKey = isDateRange ? `${weekStart}_${endDate}` : weekStart;
    if (summaries[ooId] && summaries[ooId].cacheKey === cacheKey) return;
    
    setLoadingSummary(prev => ({ ...prev, [ooId]: true }));
    try {
      const res = await ownerOperatorsApi.getSummary(ooId, weekStart, isDateRange ? endDate : null);
      setSummaries(prev => ({ ...prev, [ooId]: { ...res.data, cacheKey } }));
    } catch (error) {
      console.error('Failed to load summary:', error);
    } finally {
      setLoadingSummary(prev => ({ ...prev, [ooId]: false }));
    }
  };

  const toggleExpand = (ooId) => {
    const newExpanded = { ...expandedOO, [ooId]: !expandedOO[ooId] };
    setExpandedOO(newExpanded);
    if (newExpanded[ooId]) {
      loadSummary(ooId);
    }
  };
  
  // Navigate weeks (like Summary page)
  const navigateWeek = (direction) => {
    const newWeekStart = addDaysYMD(weekStart, direction * 7);
    setWeekStart(newWeekStart);
    setSummaries({}); // Clear cached summaries when navigating
    // If date range mode, also update end date
    if (isDateRange && endDate) {
      setEndDate(addDaysYMD(endDate, direction * 7));
    }
  };
  
  // Reload summaries for expanded items when week changes
  useEffect(() => {
    const expandedIds = Object.keys(expandedOO).filter(id => expandedOO[id]);
    if (expandedIds.length > 0) {
      expandedIds.forEach(ooId => {
        loadSummary(ooId);
      });
    }
    // Also reload mini summaries for all owner operators
    loadAllMiniSummaries();
  }, [weekStart, endDate, isDateRange]);
  
  // Load mini summaries for all owner operators (for displaying next to names)
  const [miniSummaries, setMiniSummaries] = useState({});
  
  const loadAllMiniSummaries = async () => {
    if (ownerOperators.length === 0) return;
    
    const summaryPromises = ownerOperators.map(async (oo) => {
      try {
        const res = await ownerOperatorsApi.getSummary(oo.id, weekStart, isDateRange ? endDate : null);
        return { id: oo.id, data: res.data };
      } catch (error) {
        return { id: oo.id, data: null };
      }
    });
    
    const results = await Promise.all(summaryPromises);
    const newMiniSummaries = {};
    results.forEach(({ id, data }) => {
      if (data) {
        newMiniSummaries[id] = data;
      }
    });
    setMiniSummaries(newMiniSummaries);
  };
  
  // Load mini summaries when owner operators list changes
  useEffect(() => {
    if (ownerOperators.length > 0) {
      loadAllMiniSummaries();
    }
  }, [ownerOperators]);
  
  // Toggle date range mode
  const toggleDateRangeMode = () => {
    if (!isDateRange) {
      // Switching to date range mode - default end date to current week start
      setEndDate(weekStart);
    } else {
      // Switching back to single week mode
      setEndDate(null);
    }
    setIsDateRange(!isDateRange);
    setSummaries({}); // Clear cached summaries
  };
  
  // Handle date changes
  const handleStartDateChange = (newDate) => {
    setWeekStart(newDate);
    setSummaries({});
  };
  
  const handleEndDateChange = (newDate) => {
    setEndDate(newDate);
    setSummaries({});
  };
  
  // Open PDF export dialog with custom date options
  const openPdfDialog = (ooId, ooName) => {
    setPdfOOId(ooId);
    setPdfOOName(ooName);
    setPdfStartDate(weekStart);
    setPdfEndDate(isDateRange && endDate ? endDate : weekStart);
    setPdfDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        email: formData.email || null,
        phone: formData.phone || null,
        street_address: formData.street_address || null,
        city: formData.city || null,
        state: formData.state || null,
        zipcode: formData.zipcode || null,
        tax_id: formData.tax_id || null,
        mc_number: formData.mc_number || null,
        dot_number: formData.dot_number || null,
        notes: formData.notes || null,
      };

      if (editingOO) {
        await ownerOperatorsApi.update(editingOO.id, data);
        toast.success('Owner operator updated successfully');
      } else {
        await ownerOperatorsApi.create(data);
        toast.success('Owner operator created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (oo) => {
    setEditingOO(oo);
    setFormData({
      name: oo.name,
      email: oo.email || '',
      phone: oo.phone || '',
      street_address: oo.street_address || '',
      city: oo.city || '',
      state: oo.state || '',
      zipcode: oo.zipcode || '',
      tax_id: oo.tax_id || '',
      mc_number: oo.mc_number || '',
      dot_number: oo.dot_number || '',
      status: oo.status,
      unit_ids: oo.unit_ids || [],
      notes: oo.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this owner operator?')) return;
    try {
      await ownerOperatorsApi.delete(id);
      toast.success('Owner operator deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const resetForm = () => {
    setEditingOO(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      street_address: '',
      city: '',
      state: '',
      zipcode: '',
      tax_id: '',
      mc_number: '',
      dot_number: '',
      status: 'active',
      unit_ids: [],
      notes: '',
    });
  };

  const handleUnitToggle = (unitId) => {
    const currentUnits = formData.unit_ids;
    if (currentUnits.includes(unitId)) {
      setFormData({ ...formData, unit_ids: currentUnits.filter(id => id !== unitId) });
    } else {
      setFormData({ ...formData, unit_ids: [...currentUnits, unitId] });
    }
  };

  const getUnitNumber = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit ? unit.unit_number : unitId;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const filteredOwnerOperators = ownerOperators.filter(oo =>
    (statusFilter === 'all' || (statusFilter === 'active' ? oo.status === 'active' : oo.status !== 'active')) && (
    oo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    oo.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    oo.phone?.includes(searchTerm))
  );

  const handleSetStatus = async (oo, status) => {
    if (status !== 'active' && !window.confirm(`Mark ${oo.name} inactive? Their history and past settlements stay intact.`)) return;
    try {
      await ownerOperatorsApi.setStatus(oo.id, status);
      setOwnerOperators((prev) => prev.map((o) => (o.id === oo.id ? { ...o, status } : o)));
      toast.success(`${oo.name} ${status === 'active' ? 'reactivated' : 'marked inactive'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleExportPdf = async (startDate, endDate, ooId, ooName) => {
    try {
      toast.info('Generating settlement PDF...');
      const useEndDate = endDate !== startDate ? endDate : null;
      const response = await ownerOperatorsApi.exportPdf(ooId, startDate, useEndDate, itemizedDeductions);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const filename = useEndDate 
        ? `settlement_${ooName.replace(/\s+/g, '_')}_${startDate}_to_${endDate}.pdf`
        : `settlement_${ooName.replace(/\s+/g, '_')}_${startDate}.pdf`;
      // Revoke any previously open preview URL first
      if (pdfUrl) {
        try { window.URL.revokeObjectURL(pdfUrl); } catch (_) {}
      }
      setPdfUrl(url);
      setPdfFileName(filename);
      setPdfViewerTitle(
        useEndDate
          ? `${ooName} • ${startDate} → ${endDate}`
          : `${ooName} • Week of ${startDate}`
      );
      setPdfViewerOpen(true);
      setPdfDialogOpen(false);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate settlement PDF');
    }
  };

  const handleDownloadPreviewedPdf = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = pdfFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Settlement PDF downloaded');
  };

  const closePdfViewer = () => {
    setPdfViewerOpen(false);
    if (pdfUrl) {
      try { window.URL.revokeObjectURL(pdfUrl); } catch (_) {}
      setPdfUrl(null);
    }
  };

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    inactive: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="owner-operators-loading">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="owner-operators-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-manrope font-bold text-foreground flex items-center gap-3">
            <UserCheck className="h-8 w-8 text-blue-600" />
            Owner Operators
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage owner operators and their assigned units
          </p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-owner-operator-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Owner Operator
            </Button>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-manrope">
                  {editingOO ? 'Edit Owner Operator' : 'Add New Owner Operator'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="John Doe"
                      data-testid="oo-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="oo-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      data-testid="oo-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      data-testid="oo-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="street_address">Street Address</Label>
                  <Input
                    id="street_address"
                    value={formData.street_address}
                    onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                    placeholder="123 Main St"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Dallas"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="TX"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zipcode">ZIP Code</Label>
                    <Input
                      id="zipcode"
                      value={formData.zipcode}
                      onChange={(e) => setFormData({ ...formData, zipcode: e.target.value })}
                      placeholder="75001"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tax_id">Tax ID (EIN/SSN)</Label>
                    <Input
                      id="tax_id"
                      value={formData.tax_id}
                      onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                      placeholder="XX-XXXXXXX"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mc_number">MC Number</Label>
                    <Input
                      id="mc_number"
                      value={formData.mc_number}
                      onChange={(e) => setFormData({ ...formData, mc_number: e.target.value })}
                      placeholder="MC-XXXXXX"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dot_number">DOT Number</Label>
                    <Input
                      id="dot_number"
                      value={formData.dot_number}
                      onChange={(e) => setFormData({ ...formData, dot_number: e.target.value })}
                      placeholder="XXXXXXX"
                    />
                  </div>
                </div>
                
                {/* Units Selection */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-blue-600" />
                    Units Owned
                  </Label>
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2 bg-slate-50 dark:bg-slate-800">
                    {units.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No units available</p>
                    ) : (
                      units.filter(u => u.status === 'active').map((unit) => (
                        <div key={unit.id} className="flex items-center space-x-3 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                          <Checkbox
                            id={`unit-${unit.id}`}
                            checked={formData.unit_ids.includes(unit.id)}
                            onCheckedChange={() => handleUnitToggle(unit.id)}
                          />
                          <Label htmlFor={`unit-${unit.id}`} className="flex items-center gap-2 cursor-pointer flex-1">
                            <Truck className="h-4 w-4 text-blue-600" />
                            <span className="font-medium">{unit.unit_number}</span>
                            <span className="text-xs text-muted-foreground">
                              {unit.make} {unit.model} {unit.year}
                            </span>
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formData.unit_ids.length} unit(s) selected
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="oo-submit-btn">
                    {editingOO ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Date Selector with Navigation Arrows */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search owner operators..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="oo-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[190px]" data-testid="oo-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="inactive">Inactive only</SelectItem>
                <SelectItem value="all">All owner operators</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Date Selection with Navigation */}
            <div className="flex items-center gap-2">
              {/* Back Arrow */}
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => navigateWeek(-1)}
                data-testid="week-back-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              {/* Date Toggle & Inputs */}
              <div className="flex items-center gap-2">
                <Button
                  variant={isDateRange ? "default" : "outline"}
                  size="sm"
                  onClick={toggleDateRangeMode}
                  className={isDateRange ? "bg-blue-600 hover:bg-blue-700" : ""}
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  {isDateRange ? "Range" : "Week"}
                </Button>
                
                {isDateRange ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={weekStart}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      className="w-36"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="date"
                      value={endDate || weekStart}
                      onChange={(e) => handleEndDateChange(e.target.value)}
                      className="w-36"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">Week of:</Label>
                    <Input
                      type="date"
                      value={weekStart}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      className="w-40"
                    />
                  </div>
                )}
              </div>
              
              {/* Forward Arrow */}
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => navigateWeek(1)}
                data-testid="week-forward-btn"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* PDF Export Dialog with Custom Date Selection */}
      <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Generate Settlement PDF
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Customize the date range for <strong>{pdfOOName}</strong>'s settlement PDF.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pdf-start">Start Date</Label>
                <Input
                  id="pdf-start"
                  type="date"
                  value={pdfStartDate}
                  onChange={(e) => setPdfStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pdf-end">End Date</Label>
                <Input
                  id="pdf-end"
                  type="date"
                  value={pdfEndDate}
                  onChange={(e) => setPdfEndDate(e.target.value)}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Set both dates to the same value for a single week settlement.
            </p>
            <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
              <Checkbox 
                id="oo-itemized" 
                checked={itemizedDeductions} 
                onCheckedChange={setItemizedDeductions}
                data-testid="oo-itemized-checkbox"
              />
              <Label htmlFor="oo-itemized" className="text-sm cursor-pointer">Itemized Deductions</Label>
              <span className="text-xs text-muted-foreground ml-auto">Show individual entries</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleExportPdf(pdfStartDate, pdfEndDate, pdfOOId, pdfOOName)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Generate PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Dialog — preview generated settlement PDF before downloading */}
      <Dialog open={pdfViewerOpen} onOpenChange={(o) => { if (!o) closePdfViewer(); }}>
        <DialogContent className="sm:max-w-[90vw] sm:max-h-[95vh] p-0">
          <div className="flex items-center justify-between p-4 border-b">
            <DialogTitle className="font-manrope flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Settlement Pay — {pdfViewerTitle}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleDownloadPreviewedPdf} data-testid="oo-pdf-download-btn">
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button variant="ghost" size="sm" onClick={closePdfViewer} data-testid="oo-pdf-close-btn">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="p-4">
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                className="w-full h-[calc(90vh-120px)] border rounded-lg"
                title="Owner Operator Settlement PDF"
                data-testid="oo-pdf-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Owner Operators List */}
      <div className="space-y-4">
        {filteredOwnerOperators.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <UserCheck className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {ownerOperators.length === 0 ? 'No Owner Operators Yet' : 'No Results Found'}
              </h3>
              <p className="text-muted-foreground">
                {ownerOperators.length === 0 
                  ? 'Add your first owner operator or mark a driver as an owner operator.'
                  : searchTerm ? 'Try a different search term.' : statusFilter === 'inactive' ? 'No inactive owner operators.' : 'No active owner operators — switch the filter to see inactive ones.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredOwnerOperators.map((oo) => (
            <Collapsible
              key={oo.id}
              open={expandedOO[oo.id]}
              onOpenChange={() => toggleExpand(oo.id)}
            >
              <Card className="overflow-hidden">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    {/* Stack vertically on mobile, side-by-side on >=sm so edit/delete buttons aren't hidden */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-start gap-4 min-w-0">
                        <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                          <UserCheck className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-lg font-manrope flex items-center gap-2 flex-wrap">
                            <span className="truncate">{oo.name}</span>
                            <Badge className={statusColors[oo.status]}>
                              {oo.status}
                            </Badge>
                            {oo.linked_driver_id && (
                              <Badge variant="outline" className="text-xs">
                                Driver
                              </Badge>
                            )}
                          </CardTitle>
                          <div className="flex items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1 flex-wrap">
                            {oo.email && (
                              <span className="flex items-center gap-1 truncate max-w-[200px]">
                                <Mail className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{oo.email}</span>
                              </span>
                            )}
                            {oo.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {oo.phone}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Truck className="h-3 w-3" />
                              {oo.unit_ids?.length || 0} unit(s)
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Mini Financial Summary + actions — wraps below on mobile */}
                      <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-6 flex-shrink-0">
                        {miniSummaries[oo.id] && (
                          <div className="flex items-center gap-3 sm:gap-4 text-sm">
                            {/* Weekly Summary */}
                            <div className="text-right sm:border-r sm:pr-4">
                              <p className="text-[10px] sm:text-xs text-muted-foreground">
                                {isDateRange ? 'Period' : 'Weekly'} Net
                              </p>
                              <p className={`font-mono font-bold text-sm sm:text-base ${
                                miniSummaries[oo.id].weekly_totals?.net_payout >= 0 
                                  ? 'text-emerald-600' 
                                  : 'text-red-600'
                              }`}>
                                {formatCurrency(miniSummaries[oo.id].weekly_totals?.net_payout || 0)}
                              </p>
                            </div>
                            {/* YTD Summary (mini) — only when Company toggle is ON */}
                            {showYtd && (
                            <div className="text-right">
                              <p className="text-[10px] sm:text-xs text-muted-foreground">YTD Net</p>
                              <p className={`font-mono font-bold text-sm sm:text-base ${
                                miniSummaries[oo.id].ytd_totals?.net_payout >= 0 
                                  ? 'text-blue-600' 
                                  : 'text-red-600'
                              }`}>
                                {formatCurrency(miniSummaries[oo.id].ytd_totals?.net_payout || 0)}
                              </p>
                            </div>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-1">
                        {canEdit && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 px-2 text-xs"
                              onClick={(e) => { e.stopPropagation(); handleEdit(oo); }}
                              data-testid={`edit-oo-${oo.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5 sm:mr-1" />
                              <span className="hidden sm:inline">Edit</span>
                            </Button>
                            {oo.status === 'active' ? (
                              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-slate-500 hover:text-slate-700"
                                onClick={(e) => { e.stopPropagation(); handleSetStatus(oo, 'inactive'); }} data-testid={`deactivate-oo-${oo.id}`}>
                                Deactivate
                              </Button>
                            ) : (
                              <Button variant="outline" size="sm" className="h-8 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                                onClick={(e) => { e.stopPropagation(); handleSetStatus(oo, 'active'); }} data-testid={`reactivate-oo-${oo.id}`}>
                                Reactivate
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); handleDelete(oo.id); }}
                                className="text-red-600 hover:text-red-700 h-8 w-8"
                                data-testid={`delete-oo-${oo.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                        {expandedOO[oo.id] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <CardContent className="border-t">
                    {loadingSummary[oo.id] ? (
                      <div className="py-8 text-center">
                        <Skeleton className="h-32 w-full" />
                      </div>
                    ) : summaries[oo.id] ? (
                      <div className="space-y-6 py-4">
                        {/* PDF Generation Button */}
                        {summaries[oo.id].unit_summaries?.length > 0 && (
                          <div className="flex justify-end">
                            <Button
                              onClick={(e) => { e.stopPropagation(); openPdfDialog(oo.id, oo.name); }}
                              className="bg-blue-600 hover:bg-blue-700"
                              size="sm"
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              Generate Settlement PDF
                            </Button>
                          </div>
                        )}
                        
                        {/* Period Summary */}
                        {summaries[oo.id].unit_summaries?.length > 0 ? (
                          <>
                            <div>
                              <h4 className="font-semibold text-sm flex items-center gap-2 mb-3">
                                <Calendar className="h-4 w-4 text-blue-600" />
                                {isDateRange 
                                  ? `Summary (${weekStart} to ${endDate})` 
                                  : `Weekly Summary (Week of ${weekStart})`
                                }
                              </h4>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Unit</TableHead>
                                    <TableHead className="text-right">Loads</TableHead>
                                    <TableHead className="text-right">Gross</TableHead>
                                    <TableHead className="text-right">Fees</TableHead>
                                    <TableHead className="text-right">Deductions</TableHead>
                                    <TableHead className="text-right">Net</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {summaries[oo.id].unit_summaries.map((us) => (
                                    <TableRow key={us.unit_id}>
                                      <TableCell className="font-medium">
                                        <div className="flex items-center gap-2">
                                          <Truck className="h-4 w-4 text-blue-600" />
                                          {us.unit_number}
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-right">{us.load_count}</TableCell>
                                      <TableCell className="text-right font-mono">{formatCurrency(us.total_gross)}</TableCell>
                                      <TableCell className="text-right font-mono text-blue-600">-{formatCurrency(us.total_fees)}</TableCell>
                                      <TableCell className="text-right font-mono text-red-600">-{formatCurrency(us.total_deductions)}</TableCell>
                                      <TableCell className={`text-right font-mono font-bold ${us.net_payout >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {formatCurrency(us.net_payout)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {/* Totals Row with Total Loads */}
                                  <TableRow className="bg-blue-50 dark:bg-blue-900/20 font-bold border-t-2 border-blue-200">
                                    <TableCell>
                                      <span className="flex items-center gap-2">
                                        <Building2 className="h-4 w-4 text-blue-600" />
                                        TOTAL ({summaries[oo.id].weekly_totals.unit_count} units)
                                      </span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <span className="flex items-center justify-end gap-1">
                                        <Package className="h-3 w-3" />
                                        {summaries[oo.id].weekly_totals.total_loads || 
                                          summaries[oo.id].unit_summaries.reduce((sum, us) => sum + us.load_count, 0)
                                        }
                                      </span>
                                    </TableCell>
                                    <TableCell className="text-right font-mono">{formatCurrency(summaries[oo.id].weekly_totals.total_gross)}</TableCell>
                                    <TableCell className="text-right font-mono text-blue-600">-{formatCurrency(summaries[oo.id].weekly_totals.total_fees)}</TableCell>
                                    <TableCell className="text-right font-mono text-red-600">-{formatCurrency(summaries[oo.id].weekly_totals.total_deductions)}</TableCell>
                                    <TableCell className={`text-right font-mono ${summaries[oo.id].weekly_totals.net_payout >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {formatCurrency(summaries[oo.id].weekly_totals.net_payout)}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </div>
                            
                            {/* Year-to-Date Summary — only when Company toggle is ON */}
                            {showYtd && summaries[oo.id].ytd_totals && (
                              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-800">
                                <h4 className="font-semibold text-sm flex items-center gap-2 mb-3 text-emerald-700 dark:text-emerald-400">
                                  <TrendingUp className="h-4 w-4" />
                                  Year-to-Date Summary ({summaries[oo.id].ytd_totals.year})
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg">
                                    <p className="text-xs text-muted-foreground mb-1">YTD Gross</p>
                                    <p className="font-mono font-bold text-lg">{formatCurrency(summaries[oo.id].ytd_totals.total_gross)}</p>
                                  </div>
                                  <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg">
                                    <p className="text-xs text-muted-foreground mb-1">YTD Fees</p>
                                    <p className="font-mono font-bold text-lg text-blue-600">-{formatCurrency(summaries[oo.id].ytd_totals.total_fees)}</p>
                                  </div>
                                  <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg">
                                    <p className="text-xs text-muted-foreground mb-1">YTD Deductions</p>
                                    <p className="font-mono font-bold text-lg text-red-600">-{formatCurrency(summaries[oo.id].ytd_totals.total_deductions)}</p>
                                  </div>
                                  <div className="text-center p-3 bg-emerald-100 dark:bg-emerald-800 rounded-lg">
                                    <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-1">YTD Net Earnings</p>
                                    <p className={`font-mono font-bold text-xl ${summaries[oo.id].ytd_totals.net_payout >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600'}`}>
                                      {formatCurrency(summaries[oo.id].ytd_totals.net_payout)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="py-8 text-center text-muted-foreground">
                            <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>No financial data for this week</p>
                            <p className="text-xs">Assign units to this owner operator to see summaries</p>
                          </div>
                        )}
                        
                        {/* Additional Info */}
                        {(oo.mc_number || oo.dot_number || oo.tax_id) && (
                          <div className="pt-4 border-t">
                            <h4 className="font-medium text-sm text-muted-foreground mb-2">Business Information</h4>
                            <div className="flex flex-wrap gap-4 text-sm">
                              {oo.mc_number && <span><strong>MC#:</strong> {oo.mc_number}</span>}
                              {oo.dot_number && <span><strong>DOT#:</strong> {oo.dot_number}</span>}
                              {oo.tax_id && <span><strong>Tax ID:</strong> {oo.tax_id}</span>}
                            </div>
                          </div>
                        )}
                        
                        {oo.notes && (
                          <div className="pt-4 border-t">
                            <h4 className="font-medium text-sm text-muted-foreground mb-2">Notes</h4>
                            <p className="text-sm">{oo.notes}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-muted-foreground">
                        Loading summary...
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))
        )}
      </div>
    </div>
  );
}
